import { useQuery } from '@apollo/client';
import type { DescriptionPage_DifficultyLevel as DifficultyLevel } from '__generated__/graphql-types';

import { useParams } from 'js/lib/useRouter';

import type { TopReview } from 'bundles/reviews-common/types/reviewsSharedTypes';
import courseReviewsPageQuery from 'bundles/reviews/queries/CourseReviewsPage.graphql';
import reviewsCourseHeaderDataQuery from 'bundles/reviews/queries/ReviewsCourseHeaderData.graphql';
import reviewsProductCardDataQuery from 'bundles/reviews/queries/ReviewsProductCardData.graphql';
import type {
  CourseReviewsPageCdpMetadataFragment,
  CourseReviewsPageCourseFragment,
  CourseReviewsPageQuery,
  CourseReviewsPageQueryVariables,
  CourseReviewsPageSchemaReviewFragment,
} from 'bundles/reviews/queries/__generated__/CourseReviewsPage';
import type {
  ReviewsCourseHeaderDataQuery as ReviewsCourseHeaderDataQueryResult,
  ReviewsCourseHeaderDataQueryVariables,
} from 'bundles/reviews/queries/__generated__/ReviewsCourseHeaderData';
import type {
  ReviewsProductCardDataQuery as ReviewsProductCardDataQueryResult,
  ReviewsProductCardDataQueryVariables,
} from 'bundles/reviews/queries/__generated__/ReviewsProductCardData';
import { NotFoundError } from 'bundles/ssr/lib/errors/directives';
import inServerContext from 'bundles/ssr/util/inServerContext';

type CourseReviewsPageCourseConnection = CourseReviewsPageQuery['NaptimeQueries']['XdpV1Resource']['course'];
type CourseReviewsPageCourseElement = NonNullable<NonNullable<CourseReviewsPageCourseConnection>['elements'][number]>;

export type CourseReviewsPageCourseInfo = CourseReviewsPageCourseFragment & {
  xdpMetadata: CourseReviewsPageCdpMetadataFragment;
};

export type CourseReviewsPageCdpMetadata = CourseReviewsPageCdpMetadataFragment['cdpMetadata'];
export type CourseReviewsPageSchemaReview = CourseReviewsPageSchemaReviewFragment;

export type ReviewsHeaderData = {
  averageFiveStarRating?: number | null;
  courseName: string;
  description?: string | null;
  difficultyLevel?: DifficultyLevel | null;
  imageUrl?: string | null;
  marketingProductType?: string | null;
  partnerLogoUrl?: string | null;
  partnerName?: string | null;
  ratingCount?: number | null;
  ratingCountsByStar?: {
    fiveStarRatingCount?: number | null;
    fourStarRatingCount?: number | null;
    threeStarRatingCount?: number | null;
    twoStarRatingCount?: number | null;
    oneStarRatingCount?: number | null;
  } | null;
  skillTags: string[];
  totalDuration?: string | null;
};

type CourseReviewsPageDataResult = {
  loading: boolean;
  error?: Error;
  notFound: boolean;
  courseInfo: CourseReviewsPageCourseInfo | null;
  topReviews: TopReview[];
  schemaReviews: CourseReviewsPageSchemaReview[] | null;
  headerData: ReviewsHeaderData;
};

const isDefined = <T>(value: T | null | undefined): value is T => value != null;

const isCourseReviewsPageCourseInfo = (course: CourseReviewsPageCourseElement): course is CourseReviewsPageCourseInfo =>
  course.xdpMetadata.__typename === 'XdpV1_cdpMetadataMember';

export function useCourseReviewsPageData(): CourseReviewsPageDataResult {
  const params = useParams();
  const slug = params?.slug;

  const { data, loading, error } = useQuery<CourseReviewsPageQuery, CourseReviewsPageQueryVariables>(
    courseReviewsPageQuery,
    {
      variables: { slug: slug ?? '' },
      skip: !slug,
      context: {
        ssrBlocking: true,
      },
    }
  );

  const {
    data: courseHeaderData,
    loading: courseHeaderLoading,
    error: courseHeaderError,
  } = useQuery<ReviewsCourseHeaderDataQueryResult, ReviewsCourseHeaderDataQueryVariables>(
    reviewsCourseHeaderDataQuery,
    {
      variables: { slug: slug ?? '' },
      skip: !slug,
      context: {
        clientName: 'gatewayGql',
      },
    }
  );

  const elements = (data?.NaptimeQueries?.XdpV1Resource?.course?.elements ?? []).filter(isDefined);
  const courseInfo = elements.find(isCourseReviewsPageCourseInfo) ?? null;
  const cdpMetadata = courseInfo?.xdpMetadata.cdpMetadata ?? null;
  const topReviews =
    cdpMetadata?.topReviews?.filter(
      (review): review is TopReview =>
        review?.averageFiveStarRating != null &&
        review.timestamp != null &&
        review.authorName != null &&
        review.comment != null
    ) ?? [];
  const schemaReviews = courseInfo?.fetchProductReviews?.elements?.filter(isDefined) ?? null;
  const courseId = courseInfo?.id?.split('~')[1] ?? '';

  const {
    data: productCardData,
    loading: productCardLoading,
    error: productCardError,
  } = useQuery<ReviewsProductCardDataQueryResult, ReviewsProductCardDataQueryVariables>(reviewsProductCardDataQuery, {
    variables: {
      productCardKeys: courseId ? [{ id: courseId, canonicalType: 'COURSE' as const }] : [],
    },
    skip: !courseId,
    context: {
      clientName: 'gatewayGql',
    },
  });

  const notFound = !loading && !!slug && elements.length === 0;

  if (notFound && inServerContext) {
    throw new NotFoundError();
  }

  const descriptionPage = courseHeaderData?.DescriptionPage?.queryCoursePage;
  const productCardEntry = productCardData?.ProductCard?.queryByKeys?.[0];
  const productCanonical =
    productCardEntry?.productTypeAttributes?.__typename === 'ProductCard_Course'
      ? productCardEntry.productTypeAttributes.canonical
      : null;

  const headerData: ReviewsHeaderData = {
    averageFiveStarRating:
      descriptionPage?.ratings?.averageFiveStarRating ?? cdpMetadata?.ratings?.averageFiveStarRating ?? null,
    courseName: productCanonical?.name ?? courseInfo?.name ?? '',
    description: descriptionPage?.description ?? cdpMetadata?.description ?? null,
    difficultyLevel: descriptionPage?.difficultyLevel ?? null,
    imageUrl: productCanonical?.promoPhotoUrl ?? null,
    marketingProductType: productCardEntry?.marketingProductType ?? null,
    partnerLogoUrl:
      productCanonical?.partners?.[0]?.squareLogo ??
      descriptionPage?.partners?.[0]?.squareLogo ??
      cdpMetadata?.partners?.[0]?.squareLogo ??
      cdpMetadata?.partners?.[0]?.logo ??
      null,
    partnerName:
      productCanonical?.partners?.[0]?.name ??
      descriptionPage?.partners?.[0]?.name ??
      cdpMetadata?.partners?.[0]?.name ??
      null,
    ratingCount: descriptionPage?.ratings?.ratingCount ?? cdpMetadata?.ratings?.ratingCount ?? null,
    ratingCountsByStar: descriptionPage?.ratingCountsByStar ?? null,
    skillTags: (descriptionPage?.skillTags ?? []).map((tag) => tag?.name?.trim()).filter((tag): tag is string => !!tag),
    totalDuration: descriptionPage?.material?.totalDuration ?? null,
  };

  return {
    loading: !!slug && (loading || courseHeaderLoading || (!!courseId && productCardLoading)),
    error: error ?? courseHeaderError ?? productCardError ?? undefined,
    notFound,
    courseInfo,
    topReviews,
    schemaReviews,
    headerData,
  };
}
